import grillGarden from "@/assets/grill-garden.png";
import esosGrill from "@/assets/esos-grill.png";
import breakfastRoom from "@/assets/breakfast-room.png";
import { Clock, Leaf, Flame, Coffee, Utensils, Wine, Users, Star, Check, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import { useCmsContent } from "@/hooks/useCmsContent";

const Restaurant = () => {
  const { t } = useLanguage();
  const { get } = useCmsContent();
  const rp = t.restaurantPage;

  return (
    <section id="restaurant" className="hotel-section bg-card">
      <div className="hotel-container">
        <div className="text-center max-w-3xl mx-auto mb-10 md:mb-16">
          <p className="hotel-caption text-accent mb-4">{get("restaurant_caption") || rp.caption}</p>
          <h2 className="hotel-heading text-foreground mb-6">{get("restaurant_heading") || rp.heading}</h2>
          <p className="hotel-body text-muted-foreground">{get("restaurant_text") || rp.text}</p>
        </div>
        <div className="mb-12 md:mb-20">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="relative h-64 md:h-96 rounded-lg overflow-hidden shadow-elevated">
              <img src={breakfastRoom} alt="Breakfast" loading="lazy" className="w-full h-full object-cover" />
              <div className="absolute top-4 left-4 bg-accent text-accent-foreground p-3 rounded-full"><Coffee className="w-6 h-6" /></div>
            </div>
            <div className="space-y-6">
              <div>
                <p className="hotel-caption text-accent mb-2">{rp.everyMorning}</p>
                <h3 className="font-display text-3xl font-semibold text-foreground mb-4">{get("restaurant_breakfast_title") || rp.continentalBreakfast}</h3>
                <p className="hotel-body text-muted-foreground leading-relaxed mb-4">{get("restaurant_breakfast_text") || rp.breakfastText}</p>
              </div>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 bg-accent/10 px-4 py-2 rounded-full"><Coffee className="w-4 h-4 text-accent" /><span className="font-body text-sm font-medium text-foreground">{rp.breakfastTime}</span></div>
                <div className="flex items-center gap-2 bg-accent/10 px-4 py-2 rounded-full"><Utensils className="w-4 h-4 text-accent" /><span className="font-body text-sm font-medium text-foreground">{rp.dinnerTime}</span></div>
              </div>
            </div>
          </div>
        </div>
        <div className="mb-12 md:mb-20">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="space-y-6 lg:order-1">
              <div>
                <p className="hotel-caption text-accent mb-2">{rp.ourGarden}</p>
                <h3 className="font-display text-3xl font-semibold text-foreground mb-4">{get("restaurant_garden_title") || rp.grillGarden}</h3>
                <p className="hotel-body text-muted-foreground leading-relaxed mb-4">{get("restaurant_garden_text") || rp.grillGardenText}</p>
                <p className="hotel-body text-muted-foreground leading-relaxed">{get("restaurant_grill_garden_text2") || rp.grillGardenText2}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[rp.charcoalGrill, rp.relaxedAmbience, rp.freshDrinks, rp.greenArea].map((item) => (
                  <div key={item} className="flex items-center gap-3"><Check className="w-5 h-5 text-accent" /><span className="font-body text-sm text-foreground">{item}</span></div>
                ))}
              </div>
              <div className="bg-muted/50 p-4 rounded-lg"><p className="font-body text-sm text-foreground"><span className="font-semibold">{rp.openingHours}</span> {rp.weatherOpen}</p></div>
            </div>
            <div className="relative h-64 md:h-96 rounded-lg overflow-hidden shadow-elevated lg:order-2">
              <img src={grillGarden} alt="Grill Garden" loading="lazy" className="w-full h-full object-cover" />
              <div className="absolute top-4 left-4 bg-accent text-accent-foreground p-3 rounded-full"><Leaf className="w-6 h-6" /></div>
            </div>
          </div>
        </div>
        <div className="mb-10 md:mb-16">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="relative h-64 md:h-96 rounded-lg overflow-hidden shadow-elevated">
              <img src={esosGrill} alt="Esos Grill" loading="lazy" className="w-full h-full object-cover" />
              <div className="absolute top-4 left-4 bg-accent text-accent-foreground p-3 rounded-full"><Flame className="w-6 h-6" /></div>
            </div>
            <div className="space-y-6">
              <div>
                <p className="hotel-caption text-accent mb-2">{rp.ourRestaurant}</p>
                <h3 className="font-display text-3xl font-semibold text-foreground mb-4">Esos Grill</h3>
                <p className="hotel-body text-muted-foreground leading-relaxed mb-4">{get("restaurant_esos_grill_desc") || rp.esosGrillDesc}</p>
                <p className="hotel-body text-foreground font-semibold mb-4">{get("restaurant_balkan_swiss") || rp.balkanToSwiss}</p>
                <p className="hotel-body text-muted-foreground leading-relaxed">{get("restaurant_esos_grill_desc2") || rp.esosGrillDesc2}</p>
              </div>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 bg-accent/10 px-4 py-2 rounded-full"><Clock className="w-4 h-4 text-accent" /><span className="font-body text-sm font-medium text-foreground">17:30 - 22:00</span></div>
                <div className="flex items-center gap-2 bg-accent/10 px-4 py-2 rounded-full"><Users className="w-4 h-4 text-accent" /><span className="font-body text-sm font-medium text-foreground">{rp.seats}</span></div>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-background rounded-lg p-6 md:p-8 shadow-soft">
          <h3 className="font-display text-xl md:text-2xl font-semibold text-foreground mb-6 md:mb-8 text-center">{rp.guestInfo}</h3>
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            <div className="text-center">
              <div className="w-14 h-14 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4"><Phone className="w-7 h-7 text-accent" /></div>
              <h4 className="font-display font-semibold text-foreground mb-2">{rp.tableReservation}</h4>
              <p className="font-body text-sm text-muted-foreground mb-3">{rp.tableReservationDesc}</p>
              <Button variant="hotel" size="sm" asChild><Link to="/contact">{rp.reserveTable}</Link></Button>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4"><Star className="w-7 h-7 text-accent" /></div>
              <h4 className="font-display font-semibold text-foreground mb-2">{rp.nonGuests}</h4>
              <p className="font-body text-sm text-muted-foreground">{rp.nonGuestsDesc}</p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4"><Wine className="w-7 h-7 text-accent" /></div>
              <h4 className="font-display font-semibold text-foreground mb-2">{rp.drinksBeer}</h4>
              <p className="font-body text-sm text-muted-foreground">{rp.drinksBeerDesc}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Restaurant;
